import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import type { WavePlanRefreshDiff } from '@/api/wavePlans'
import { staleReasonLabel } from './staleReasons'
import {
  useRefreshWavePlan,
  useWavePlanRefreshPreview,
} from '@/hooks/useWavePlans'

function DiffSummary({ diff }: { diff: WavePlanRefreshDiff }) {
  const items: [string, number | string][] = [
    ['Added lines', diff.added_lines.length],
    ['Removed lines', diff.removed_lines.length],
    ['Changed lines', diff.changed_lines.length],
    [
      'Stores added / removed',
      `${diff.population.stores_added} / ${diff.population.stores_removed}`,
    ],
    ['Stores moved to another DC', diff.population.stores_moved_dc],
    [
      'Weeks added / removed',
      `${diff.timeline.weeks_added.length} / ${diff.timeline.weeks_removed.length}`,
    ],
  ]
  return (
    <div className="space-y-3 text-sm">
      {diff.material ? (
        <p className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          Material changes:{' '}
          {diff.material_reasons.map(staleReasonLabel).join(', ')}.
        </p>
      ) : (
        <p className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
          No material changes since the last refresh.
        </p>
      )}
      <dl className="grid grid-cols-2 gap-x-6 gap-y-2">
        {items.map(([label, value]) => (
          <div key={label}>
            <dt className="text-[11px] text-muted-foreground">{label}</dt>
            <dd className="font-medium tabular-nums">{value}</dd>
          </div>
        ))}
      </dl>
      {diff.changed_lines.length > 0 && (
        <div className="max-h-40 overflow-y-auto rounded-md border">
          <table className="w-full text-xs">
            <tbody>
              {diff.changed_lines.slice(0, 50).map(line => (
                <tr key={line.line_ref_id} className="border-b last:border-0">
                  <td className="px-2 py-1 font-medium">
                    {line.sku_number ?? '—'}
                  </td>
                  <td className="px-2 py-1 text-muted-foreground">
                    {Object.entries(line.fields)
                      .map(
                        ([field, change]) =>
                          `${field}: ${String(change.old ?? '—')} → ${String(change.new ?? '—')}`
                      )
                      .join(' · ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[11px] text-muted-foreground">
        Planner overrides are kept for lines that remain. The current state is
        saved as a revision before refreshing.
      </p>
    </div>
  )
}

export function WavePlanRefreshDialog({
  planId,
  open,
  onOpenChange,
  onCloseAutoFocus,
}: {
  planId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onCloseAutoFocus?: (event: Event) => void
}) {
  const preview = useWavePlanRefreshPreview(planId, open)
  const refresh = useRefreshWavePlan(planId)
  return (
    <Dialog
      open={open}
      onOpenChange={refresh.isPending ? undefined : onOpenChange}
    >
      <DialogContent
        className="overflow-y-auto sm:max-w-[560px]"
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <DialogHeader>
          <DialogTitle>Refresh from assortment plan</DialogTitle>
          <DialogDescription>
            Review what changed in the assortment plan since this wave plan was
            last refreshed.
          </DialogDescription>
        </DialogHeader>
        {preview.isError ? (
          <div className="flex flex-col items-start gap-2 py-6 text-sm">
            <p className="text-destructive">
              The comparison with the assortment plan failed.
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => void preview.refetch()}
            >
              Try again
            </Button>
          </div>
        ) : preview.isLoading || !preview.data ? (
          <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 size={16} className="animate-spin" /> Comparing…
          </div>
        ) : (
          <DiffSummary diff={preview.data.diff} />
        )}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={refresh.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={() =>
              preview.data &&
              // Apply exactly what was previewed: a newer version is a conflict.
              refresh.mutate(preview.data.inputs_version, {
                onSuccess: () => onOpenChange(false),
              })
            }
            disabled={refresh.isPending || !preview.data}
          >
            {refresh.isPending ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <RefreshCw size={14} />
            )}
            Refresh
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
